/**
 * Transform reasons data to a format that can be used by the vuetify data table to display the reasons
 * @param reasons - Reasons data from the API
 * @returns - formatted reasons data
 */
export const transformReasonsData = (reasons) => {
  const updatedReasons = reasons.reduce((acc, item) => {
    const { doc_name: docName, parameter, reason } = item
    if (docName) {
      const formattedDocName = docName.toLowerCase().replace(' ', '_')
      const updatedReason = parameter + ' ' + reason

      if (!acc) {
        acc = {}
      }

      if (!acc[formattedDocName]) {
        acc[formattedDocName] = []
      }
      if (!acc['ALL_REASONS']) {
        acc['ALL_REASONS'] = []
      }
      acc['ALL_REASONS'].push(updatedReason)
      acc[formattedDocName].push({ ...item, label: updatedReason, value: updatedReason })

      return acc
    }
  }, {})

  // Remove duplicates from ALL_REASONS
  updatedReasons['ALL_REASONS'] = [...new Set(updatedReasons['ALL_REASONS'])]

  return updatedReasons
}

/**
 * Transform selections data to a format that can be used as payload in the api
 * @param selections - Selections data from the document
 * @param captureScreenshot - Function to capture the screenshot of the selected area
 * @returns - formatted selections data
 */
export const transformSelections = (selections, captureScreenshot) => {
  if (!selections) return []

  return selections.map(rect => {
    return {
      pdf_page_number: rect.pdf_page_number,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      reason: rect.reason,
      selection_screenshot: captureScreenshot(rect.pdf_page_number, rect)
    }
  })
}

/**
 * Convert base64 string to file
 * @param base64Data - Base64 string
 * @param filename - File name
 * @returns - File object
 */
export const convertBase64ToFile = (base64Data, filename = '') => {
  if (!base64Data) {
    throw new Error('No base64 data provided')
  }

  const [header, data] = base64Data.split(',')
  if (!header || !data) {
    throw new Error('Invalid base64 format')
  }

  const mimeMatch = header.match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'

  const byteString = atob(data)
  const u8arr = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    u8arr[i] = byteString.charCodeAt(i)
  }

  // Generate filename if not provided
  if (!filename) {
    const extensionMap = {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'application/pdf': 'pdf',
      'text/plain': 'txt',
      'application/json': 'json',
      'application/octet-stream': 'bin'
    }

    const ext = extensionMap[mime] || 'bin'
    filename = `file-${Date.now()}.${ext}`
  }

  return new File([u8arr], filename, { type: mime })
}

/**
 * Get reason options for a given document type
 * @param reasonFieldData - Reasons data from the API
 * @param docType - Document type
 * @returns - Array of reason options
 */
export const getReasonOptionsForDocumentType = (reasonFieldData, docType) => {
  const documentNames = (docType || '').split(',')
  let fieldOptions = []

  if (reasonFieldData) {
    documentNames.forEach((name) => {
      const updatedDocType = name.toLowerCase().replace(' ', '_')
      const tempOptions = reasonFieldData[updatedDocType]

      if (Array.isArray(tempOptions)) {
        fieldOptions = fieldOptions.concat(tempOptions)
      }
    })
  }

  if (fieldOptions.length) {
    const uniqueOptions = [...new Set(fieldOptions.map(option => option.value))]
    fieldOptions = uniqueOptions.map(value => ({ label: value, value }))
  }

  return fieldOptions
}
